/** @format */

import * as Sentry from "@sentry/react";
import { Component, type ErrorInfo, type ReactNode } from "react";

import { queryClient } from "../queryClient";

interface InlineErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback UI. When provided, replaces the default error card. */
  fallback?: ReactNode;
  /** Optional callback invoked when the user clicks "Try again". */
  onReset?: () => void;
  /** Optional label for what this boundary wraps (e.g., "Map", "Scanner"). */
  name?: string;
}

interface InlineErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Compact error boundary for wrapping widgets, sections, and independent UI areas.
 * Shows a small inline error card instead of a full-page error screen.
 * Reports caught errors to Sentry automatically.
 */
class InlineErrorBoundary extends Component<
  InlineErrorBoundaryProps,
  InlineErrorBoundaryState
> {
  constructor(props: InlineErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): InlineErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      tags: { errorBoundary: "inline", component: this.props.name ?? "unknown" },
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const { name } = this.props;
    const { error } = this.state;
    let isStaff = false;
    try {
      isStaff = queryClient.getQueryData<User>(["user"])?.isStaff ?? false;
    } catch {
      // queryClient may not be available
    }

    return (
      <div className="flex items-center justify-center w-full h-full min-h-[80px] p-3">
        <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-4 max-w-sm w-full text-center">
          <div className="text-red-400 text-lg mb-1">&#x26A0;</div>
          <p className="text-sm font-medium text-slate-200 mb-1">
            {name ? `Error in ${name}` : "Something went wrong"}
          </p>
          {isStaff && error && (
            <p
              className="text-xs text-red-400/80 font-mono mb-2 truncate"
              title={error.message}
            >
              {error.message}
            </p>
          )}
          <button
            type="button"
            className="text-xs text-red-400 border border-red-400/50 rounded px-3 py-1 hover:bg-red-400/10"
            onClick={this.handleReset}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}

export default InlineErrorBoundary;
